import React, { useState, useEffect } from 'react';
import './PhyidList.css';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  TextField,
  IconButton,
  Tooltip,
  Drawer,
  Box,
  Typography,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Cookies from 'js-cookie';

const PhyidList = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useAuth();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentRecordId, setCurrentRecordId] = useState(null);
  const [originalRecord, setOriginalRecord] = useState(null);
  const [formData, setFormData] = useState({
    phyid: '',
    phyname: '',
    real_cert_type: '0',
    seq: 0
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error'
  });
  
  // Add sort configuration state
  const [sortConfig, setSortConfig] = useState({
    key: 'seq',  // Default sort column
    direction: 'ascending'  // Default sort direction
  });

  const getCertTypeText = (type) => {
    const certTypes = {
      '0': '沒正本',
      '1': '戶口名簿',
      '2': '戶籍',
      '3': '身分證',
      '4': '健保卡',
      '5': '駕照'
    };
    return certTypes[type] || '未知類型';
  };

  useEffect(() => {
    const fetchPhyidList = async () => {
      try {
        const token = Cookies.get('jwt_token');
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/phyid/get`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setData(response.data.data);
        setFilteredData(response.data.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching phyid list:', err);
        let errorMessage = '載入資料失敗';
        
        if (err.response) {
          if (err.response.status === 401) {
            errorMessage = '認證失敗，請重新登入';
            setSnackbar({
              open: true,
              message: errorMessage,
              severity: 'error'
            });
            setLoading(false);
            return;
          } else if (err.response.status === 500) {
            errorMessage = '伺服器錯誤，請稍後再試';
            setSnackbar({
              open: true,
              message: errorMessage,
              severity: 'error'
            });
            setLoading(false);
            return;
          } else {
            errorMessage = err.response.data?.message || `錯誤 (${err.response.status})`;
          }
        } else if (err.request) {
          errorMessage = '無法連接到伺服器，請檢查網路連接';
        }
        
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
        setError(err.message);
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchPhyidList();
    }
  }, [isAuthenticated]);

  // Filter data based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredData(data);
    } else {
      const filtered = data.filter(item => 
        item.phyid.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.phyname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getCertTypeText(item.real_cert_type).toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredData(filtered);
    }
  }, [searchTerm, data]);

  // Handle sorting when column header is clicked
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Get sort indicator for column headers
  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
    }
    return '';
  };

  // Sort the data based on current sort configuration
  const sortedData = React.useMemo(() => {
    if (!Array.isArray(filteredData)) {
      return [];
    }
    
    return [...filteredData].sort((a, b) => {
      if (!sortConfig.key) return 0;
      
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  const handleFileUpload = async (event, id, phyid) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file extension
    const allowedExtensions = ['jpg', 'jpeg'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    if (!allowedExtensions.includes(fileExtension)) {
      setSnackbar({
        open: true,
        message: '只允許上傳 jpg, jpeg 格式的檔案',
        severity: 'error'
      });
      return;
    }

    try {
      const token = Cookies.get('jwt_token');
      const formData = new FormData();
      formData.append('id', id);
      formData.append('phyid', phyid);
      formData.append('id_pic', file);

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/upload/idpic`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.status === 200) {
        // Update the has_id_pic status for the specific row
        setData(prevData => 
          prevData.map(item => 
            item.id === id 
              ? { ...item, has_id_pic: true }
              : item
          )
        );
        setFilteredData(prevData => 
          prevData.map(item => 
            item.id === id 
              ? { ...item, has_id_pic: true }
              : item
          )
        );

        setSnackbar({
          open: true,
          message: '上傳成功',
          severity: 'success'
        });
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      let errorMessage = '上傳失敗';
      
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = '認證失敗，請重新登入';
        } else if (err.response.status === 403) {
          errorMessage = '您沒有權限執行此操作';
        } else if (err.response.status === 500) {
          errorMessage = '伺服器錯誤，請稍後再試';
        } else {
          errorMessage = err.response.data?.message || `錯誤 (${err.response.status})`;
        }
      } else if (err.request) {
        errorMessage = '無法連接到伺服器，請檢查網路連接';
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleModify = (id) => {
    // Find the record with the given ID
    const record = data.find(item => item.id === id);
    if (record) {
      // Store the original record data
      setOriginalRecord({...record});
      // Set the form data with the record's values
      setFormData({
        phyid: record.phyid,
        phyname: record.phyname,
        real_cert_type: record.real_cert_type,
        seq: record.seq
      });
      setCurrentRecordId(id);
      setIsEditMode(true);
      setIsDrawerOpen(true);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleAddNew = () => {
    // Reset form data for new record
    setFormData({
      phyid: '',
      phyname: '',
      real_cert_type: '0',
      seq: 0  // Keep default value for form state
    });
    setIsEditMode(false);
    setCurrentRecordId(null);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setFormData({
      phyid: '',
      phyname: '',
      real_cert_type: '0',
      seq: 0
    });
    setIsEditMode(false);
    setCurrentRecordId(null);
    setOriginalRecord(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = Cookies.get('jwt_token');
      let response;
      
      if (isEditMode) {
        // Find differences between original and updated values
        const changedValues = {
          id: currentRecordId  // Include the id field for the backend
        };
        let hasChanges = false;
        
        // Compare each field and only include changed values
        if (formData.phyid !== originalRecord.phyid) {
          changedValues.phyid = formData.phyid;
          hasChanges = true;
        }
        
        if (formData.phyname !== originalRecord.phyname) {
          changedValues.phyname = formData.phyname;
          hasChanges = true;
        }
        
        if (formData.real_cert_type !== originalRecord.real_cert_type) {
          changedValues.real_cert_type = formData.real_cert_type;
          hasChanges = true;
        }
        
        if (formData.seq !== originalRecord.seq) {
          changedValues.seq = formData.seq;
          hasChanges = true;
        }
        
        // Only make the API call if there are changes
        if (hasChanges) {
          // Update existing record with only changed values
          response = await axios.put(
            `${process.env.REACT_APP_API_BASE_URL}/api/phyid/mod`, 
            changedValues, 
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
        } else {
          // No changes were made, just close the drawer
          handleCloseDrawer();
          setSnackbar({
            open: true,
            message: '沒有變更',
            severity: 'info'
          });
          return;
        }
      } else {
        // Add new record - exclude seq field
        const newRecordData = {
          phyid: formData.phyid,
          phyname: formData.phyname,
          real_cert_type: formData.real_cert_type
        };
        
        response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/phyid/add`, 
          newRecordData, 
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
      }
      
      if (response.status === 200) {
        // Refresh the list after adding/updating
        try {
          const listResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/phyid/get`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          setData(listResponse.data.data);
          setFilteredData(listResponse.data.data);
          handleCloseDrawer();
          
          // Show success message
          setSnackbar({
            open: true,
            message: isEditMode ? '更新成功' : '新增成功',
            severity: 'success'
          });
        } catch (listErr) {
          console.error('Error refreshing phyid list:', listErr);
          let errorMessage = '操作成功但載入資料失敗';
          
          if (listErr.response) {
            if (listErr.response.status === 401) {
              errorMessage = '認證失敗，請重新登入';
            } else if (listErr.response.status === 500) {
              errorMessage = '伺服器錯誤，請稍後再試';
            } else {
              errorMessage = `伺服器錯誤，請稍後再試`;
            }
          } else if (listErr.request) {
            errorMessage = '伺服器錯誤，請稍後再試';
          }
          
          setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'warning'
          });
          handleCloseDrawer();
        }
      } else {
        setSnackbar({
          open: true,
          message: response.data.message || (isEditMode ? '更新失敗' : '新增失敗'),
          severity: 'error'
        });
      }
    } catch (err) {
      console.error('Error updating phyid:', err);
      let errorMessage = '發生錯誤';
      
      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (err.response.status === 401) {
          errorMessage = '認證失敗，請重新登入';
        } else if (err.response.status === 500) {
          errorMessage = '伺服器錯誤，請稍後再試';
        } else {
          errorMessage = err.response.data?.message || `錯誤 (${err.response.status})`;
        }
      } else if (err.request) {
        // The request was made but no response was received
        errorMessage = '無法連接到伺服器，請檢查網路連接';
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleDelete = async () => {
    try {
      const deletedData = {
        id: currentRecordId,
      };
      const token = Cookies.get('jwt_token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/phyid/del`,
        deletedData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.status === 200) {
        // Refresh the list after deleting
        try {
          const listResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/phyid/get`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          setData(listResponse.data.data);
          setFilteredData(listResponse.data.data);
          handleCloseDrawer();
          
          setSnackbar({
            open: true,
            message: '刪除成功',
            severity: 'success'
          });
        } catch (listErr) {
          console.error('Error refreshing phyid list after delete:', listErr);
          let errorMessage = '刪除成功但載入資料失敗';
          
          if (listErr.response) {
            if (listErr.response.status === 401) {
              errorMessage = '認證失敗，請重新登入';
            } else if (listErr.response.status === 500) {
              errorMessage = '伺服器錯誤，請稍後再試';
            } else {
              errorMessage = `伺服器錯誤，請稍後再試`;
            }
          } else if (listErr.request) {
            errorMessage = '伺服器錯誤，請稍後再試';
          }
          
          setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'warning'
          });
          handleCloseDrawer();
        }
      }
    } catch (err) {
      console.error('Error deleting phyid:', err);
      let errorMessage = '刪除失敗';
      
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = '認證失敗，請重新登入';
        } else if (err.response.status === 403) {
          errorMessage = '您沒有權限執行此操作';
        } else if (err.response.status === 500) {
          errorMessage = '伺服器錯誤，請稍後再試';
        } else {
          errorMessage = err.response.data?.message || `錯誤 (${err.response.status})`;
        }
      } else if (err.request) {
        errorMessage = '無法連接到伺服器，請檢查網路連接';
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="phyidlist-container">
      <h1 className="phyidlist-title">身分證列表</h1>
      
      {/* <div className="search-section">
        <TextField
          className="search-input"
          placeholder="搜尋身分證字號、姓名或證件類型"
          value={searchTerm}
          onChange={handleSearchChange}
          variant="outlined"
          InputProps={{
            startAdornment: <SearchIcon style={{ marginRight: '8px', color: '#666' }} />,
          }}
        />
      </div> */}
      
      <div className="table-header-container">
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
          className="add-button"
        >
          新增
        </Button>
      </div>
      
      <TableContainer component={Paper} className="table-container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell 
                onClick={() => handleSort('phyid')}
                style={{ cursor: 'pointer' }}
              >
                身分證字號{getSortIndicator('phyid')}
              </TableCell>
              <TableCell 
                onClick={() => handleSort('phyname')}
                style={{ cursor: 'pointer' }}
              >
                姓名{getSortIndicator('phyname')}
              </TableCell>
              <TableCell 
                onClick={() => handleSort('real_cert_type')}
                style={{ cursor: 'pointer' }}
              >
                證件{getSortIndicator('real_cert_type')}
              </TableCell>
              <TableCell 
                onClick={() => handleSort('seq')}
                style={{ cursor: 'pointer' }}
              >
                順序{getSortIndicator('seq')}
              </TableCell>
              <TableCell>功能</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.phyid}</TableCell>
                <TableCell>{row.phyname}</TableCell>
                <TableCell>{getCertTypeText(row.real_cert_type)}</TableCell>
                <TableCell>{row.seq}</TableCell>
                <TableCell>
                  <input
                    type="file"
                    id={`file-upload-${row.id}`}
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileUpload(e, row.id, row.phyid)}
                    accept=".jpg,.jpeg"
                  />
                  <label htmlFor={`file-upload-${row.id}`}>
                    <Tooltip title="編輯">
                      <IconButton
                        color="primary"
                        onClick={() => handleModify(row.id)}
                        className="modify-icon-button"
                        style={{ color: '#000000' }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={row.has_id_pic ? "已上傳證件" : "上傳證件"}>
                      <IconButton
                        color="primary"
                        component="span"
                        className="upload-icon-button"
                        style={{ color: row.has_id_pic ? '#4caf50' : undefined }}
                      >
                        {row.has_id_pic ? <CheckCircleIcon /> : <CloudUploadIcon />}
                      </IconButton>
                    </Tooltip>
                  </label>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: {
              xs: '50%',  // Full width on mobile
              sm: '30%',   // 80% width on small tablets
              md: '300px'  // Fixed width on larger screens
            },
            maxWidth: '300px',
            padding: {
              xs: 2,
              sm: 3
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">{isEditMode ? '編輯身分證' : '新增身分證'}</Typography>
          <IconButton onClick={handleCloseDrawer}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="身分證字號"
              name="phyid"
              value={formData.phyid}
              onChange={handleInputChange}
              required
              fullWidth
              error={formData.phyid.trim() === ''}
              helperText={formData.phyid.trim() === '' ? "身分證字號不能為空" : ""}
            />
            <TextField
              label="姓名"
              name="phyname"
              value={formData.phyname}
              onChange={handleInputChange}
              required
              fullWidth
              error={formData.phyname.trim() === ''}
              helperText={formData.phyname.trim() === '' ? "姓名不能為空" : ""}
            />
            <FormControl fullWidth>
              <InputLabel id="cert-type-label">證件</InputLabel>
              <Select
                labelId="cert-type-label"
                name="real_cert_type"
                value={formData.real_cert_type}
                onChange={handleInputChange}
                label="證件"
              >
                <MenuItem value="0">沒正本</MenuItem>
                <MenuItem value="1">戶口名簿</MenuItem>
                <MenuItem value="2">戶籍</MenuItem>
                <MenuItem value="3">身分證</MenuItem>
                <MenuItem value="4">健保卡</MenuItem>
                <MenuItem value="5">駕照</MenuItem>
              </Select>
            </FormControl>
            {isEditMode && (
              <TextField
                label="順序"
                name="seq"
                type="number"
                value={formData.seq}
                onChange={handleInputChange}
                required
                fullWidth
                inputProps={{ min: 1 }}
                error={formData.seq <= 0}
                helperText={formData.seq <= 0 ? "順序必須大於0" : ""}
              />
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
            >
              {isEditMode ? '確認更新' : '確認新增'}
            </Button>
            {isEditMode && (
              <Button
                variant="contained"
                color="error"
                fullWidth
                sx={{ mt: 2, backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#b71c1c' } }}
                onClick={handleDelete}
              >
                刪除
              </Button>
            )}
          </Box>
        </form>
      </Drawer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default PhyidList; 